import { apiFetch } from './api.js'
import { goePowerW } from '../config/goePower.js'
import { hueOutdoor, plausibleOutdoor, HUE_OUTDOOR_PATTERN } from '../config/outdoorTemp.js'
import { freshPvW } from '../config/pvFresh.js'

const BASE = (import.meta.env.VITE_API_BASE ?? '') + '/iobroker'
const TAHOMA_BASE = (import.meta.env.VITE_API_BASE ?? '') + '/tahoma'

// ── EEL battery box aggregation ───────────────────────────────────────────────
// Aggregates power (sum) and SoC/temperature/voltage (average) across all active
// EEL boxes (active = Voltage > 0) from an ioBroker states object.
// Returns { sumPower, avgPower, avgSoc, avgTemperature, avgVoltage }.
function aggregateEelStates(states) {
  const activeKeys = Object.keys(states)
    .filter(k => /\.EEL\.Box\d+\.Voltage$/.test(k))
    .filter(k => (states[k]?.val ?? 0) > 0)
  const avg = (field) => {
    const vals = activeKeys
      .map(k => states[k.replace('.Voltage', `.${field}`)]?.val)
      .filter(v => v != null)
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null
  }
  const sum = (field) => {
    const vals = activeKeys
      .map(k => states[k.replace('.Voltage', `.${field}`)]?.val)
      .filter(v => v != null)
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) : null
  }
  return {
    sumPower:       sum('Power'),
    avgPower:       avg('Power'),
    avgSoc:         avg('SOC'),
    avgTemperature: avg('Temperature'),
    avgVoltage:     avg('Voltage'),
  }
}

const GO_E_CAR = { 1: 'Ready', 2: 'Charging', 3: 'Waiting', 4: 'Finished' }
const WASHER_ID = 'f34546ba-6a2c-39ef-e0b6-b6c816f03e6e'
const DRYER_ID  = '1064f08b-2119-976e-7626-6f1f48b40552'
const TADO1_HOME = '1742381'
// Second Tado home (Bad, Sportzimmer) — on tado.1 since the accounts were merged.
const TADO_HOME2 = '1402967'

async function getPlain(id) {
  try {
    const r = await apiFetch(`${BASE}/value/${encodeURIComponent(id)}`)
    const text = await r.text()
    if (text.startsWith('error')) return null
    return JSON.parse(text)
  } catch {
    return null
  }
}

export async function getStates(pattern) {
  try {
    const r = await apiFetch(`${BASE}/states?pattern=${encodeURIComponent(pattern)}`)
    if (!r.ok) return {}
    return await r.json()
  } catch {
    return {}
  }
}

function val(states, key) {
  return states[key]?.val ?? null
}

// Last-change timestamp (ms) — when a datapoint's value last actually changed,
// preserved across restarts. See server/dashboard-transform.js (kept in sync).
function lc(states, key) {
  return states[key]?.lc ?? null
}

// °C sits under `.value` in objects from older adapter versions, `.celsius` in newer ones.
const degC = (states, base) => val(states, `${base}.value`) ?? val(states, `${base}.celsius`)

function parseTadoRoom(states, home, roomId, id = roomId) {
  const pre = `tado.1.${home}.Rooms.${roomId}`
  const name = val(states, `${pre}.name`)
  if (!name) return null
  return {
    id,
    name: name.replace(/"/g, ''),
    temp:         degC(states, `${pre}.sensorDataPoints.insideTemperature`),
    humidity:     val(states, `${pre}.sensorDataPoints.humidity.percentage`),
    target:       degC(states, `${pre}.setting.temperature`),
    power:        val(states, `${pre}.setting.power`),
    heatingPower: val(states, `${pre}.heatingPower.percentage`),
    openWindow:   val(states, `${pre}.openWindow.activated`),
    nextTarget:   degC(states, `${pre}.nextScheduleChange.setting.temperature`),
  }
}

const parseTado1Room = (states, roomId) => parseTadoRoom(states, TADO1_HOME, roomId)

// Room ids present under a home — Tado numbers rooms freely, so don't assume 1..8.
function tadoRoomIds(states, home) {
  const ids = new Set()
  const re = new RegExp(`^tado\\.1\\.${home}\\.Rooms\\.(\\d+)\\.name$`)
  for (const k of Object.keys(states)) { const m = re.exec(k); if (m) ids.add(Number(m[1])) }
  return [...ids].sort((a, b) => a - b)
}

const HEATPUMP_PREFIX = 'mqtt.4.viessmann.vitocal.'
const HEATPUMP_SHELLY_POWER = 'shelly.0.shellypro3em#e05a1b333574#1.EM0.TotalActivePower'
// Above this the compressor must be running (standby 6–18 W, pumps ≤ ~80 W)
const HEATPUMP_RUNNING_W = 150

function parseHeatpump(hpStates, shellyEmStates = {}, hueStates = {}) {
  // Flat topic map published by viessmann_mqtt.py on pi2 (Vitocal 200-S via
  // Optolink). The `json` summary topic is skipped — individual topics carry
  // the same values. Numeric strings are coerced so the card gets numbers.
  // Mirror of parseHeatpump in server/dashboard-transform.js — keep in sync.
  const values = {}
  let lastTs = null
  for (const [key, state] of Object.entries(hpStates)) {
    if (!key.startsWith(HEATPUMP_PREFIX)) continue
    const name = key.slice(HEATPUMP_PREFIX.length)
    if (name === 'json' || name.includes('.')) continue
    let v = state?.val ?? null
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) v = Number(v)
    values[name] = v
    if (name === 'tempA' && state?.ts) lastTs = state.ts
  }
  // Live electric power from the Shelly Pro 3EM on the heat pump circuit —
  // the Vitocal exposes no live watts via Optolink, only cumulative kWh.
  const powerW = shellyEmStates[HEATPUMP_SHELLY_POWER]?.val ?? null
  if (powerW != null) values.shellyPowerW = Math.round(powerW)
  // Outdoor: the Hue sensors at the house; the Vitocal's own only as fallback, glitches dropped.
  if ('tempA' in values) values.tempA = plausibleOutdoor(values.tempA)
  const hue = hueOutdoor(hueStates)
  const outdoor = hue?.c ?? values.tempA ?? null
  if (outdoor != null) {
    values.tempOutdoor = outdoor
    values.tempOutdoorSource = hue ? `Hue (${hue.n} Sensoren)` : 'Vitocal'
  }
  return {
    online: values.status === 'online',
    running: values.running === 1 || (powerW != null && powerW > HEATPUMP_RUNNING_W),
    values,
    lastTs,
  }
}

function parsePlanters(zigbeeStates) {
  return Object.entries(zigbeeStates)
    .filter(([key]) => key.startsWith('mqtt.0.zigbee2mqtt.') && !key.slice(19).includes('/'))
    .map(([key, state]) => {
      const name = key.split('.').pop()
      const raw = state?.val
      if (!raw) return null
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        if (parsed.soil_moisture == null) return null
        return {
          name,
          moisture:    parsed.soil_moisture,
          battery:     parsed.battery       ?? null,
          linkquality: parsed.linkquality   ?? null,
          temperature: parsed.temperature   ?? null,
          lastSeen:    state?.ts ?? null,
        }
      } catch {
        return null
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name))
}

function parseValves(zigbeeStates) {
  const parse = (key) => {
    const raw = zigbeeStates[`mqtt.0.zigbee2mqtt.${key}`]?.val
    if (!raw) return { on: false, battery: null, linkquality: null }
    try {
      const { state, battery, linkquality, update, update_available, ...rest } = typeof raw === 'string' ? JSON.parse(raw) : raw
      return { on: state === 'ON', battery: battery ?? null, linkquality: linkquality ?? null, ...rest }
    } catch { return { on: false, battery: null, linkquality: null } }
  }
  return {
    front: parse('valve_front'),
    back:  parse('valve_back'),
  }
}

/**
 * Transform a statesMap (pattern → states object, as broadcast by SSE) into the
 * same shape returned by fetchDashboardData. Used by both the SSE path (DataContext
 * derives data from LiveDataContext) and the HTTP fallback.
 */
export function transformDashboardData(statesMap, carForceAmpere = 0) {
  const solarStates    = statesMap['mqtt.0.solar.*']    ?? {}
  const goeStates      = statesMap['go-e.0.*']          ?? {}
  const washerStates   = statesMap[`smartthings.0.${WASHER_ID}.status.main.*`]  ?? {}
  const dryerStates    = statesMap[`smartthings.0.${DRYER_ID}.status.main.*`]   ?? {}
  const nsPanelStates  = statesMap['alias.0.NSPanel.*'] ?? {}
  const tado1States    = statesMap[`tado.1.${TADO1_HOME}.Rooms.*`]              ?? {}
  const tado2States    = statesMap[`tado.1.${TADO_HOME2}.Rooms.*`]                    ?? {}
  const settingsStates = statesMap['0_userdata.0.yokan-energy.settings.*']      ?? {}
  const goeMqttStates  = statesMap['mqtt.0.go-e.yokan.*']                       ?? {}
  const zigbeeStates   = statesMap['mqtt.0.zigbee2mqtt.*']                       ?? {}
  const heatpumpStates = statesMap['mqtt.4.viessmann.vitocal.*']                 ?? {}
  const heatpumpEmStates = statesMap['shelly.0.shellypro3em#e05a1b333574#1.EM0.*'] ?? {}

  // nrg[11], 0 unless charging — `wo` froze at 10 kW through a phase error (goePower.js)
  const wallboxPowerW  = goePowerW({ nrg: val(goeMqttStates, 'mqtt.0.go-e.yokan.nrg'), car: val(goeMqttStates, 'mqtt.0.go-e.yokan.car'), wo: val(goeMqttStates, 'mqtt.0.go-e.yokan.wo') })
  const wallboxAmpMqtt = val(goeMqttStates, 'mqtt.0.go-e.yokan.amp')

  const tado1Rooms = []
  for (const i of tadoRoomIds(tado1States, TADO1_HOME)) {
    const room = parseTado1Room(tado1States, i)
    if (room) tado1Rooms.push(room)
  }

  // The second Tado home (Bad, Sportzimmer). Read through tado.1 since the two Tado
  // accounts were merged (Sep 2026); tado.0 is retired. Key kept for the cards.
  const tado0Rooms = []
  for (const i of tadoRoomIds(tado2States, TADO_HOME2)) {
    const room = parseTadoRoom(tado2States, TADO_HOME2, i, `tado0-${i}`)
    if (room && room.temp != null) tado0Rooms.push(room)
  }

  const solarAcPower = val(solarStates, 'mqtt.0.solar.ac.power') ?? 0
  const solarYieldToday = Object.entries(solarStates)
    .filter(([k]) => k.endsWith('.yieldday'))
    .reduce((sum, [, v]) => sum + (Number(v?.val) || 0), 0)

  const carStatus     = val(goeStates, 'go-e.0.car')
  const allowCharging = val(goeStates, 'go-e.0.allow_charging')
  const chargePowerW  = wallboxPowerW != null ? wallboxPowerW : (val(goeStates, 'go-e.0.energy.power') ?? 0) * 1000
  const chargeTotal   = val(goeStates, 'go-e.0.energy.total') ?? 0
  const chargeAmpere  = val(goeStates, 'go-e.0.ampere') ?? 0

  const washerState       = val(washerStates, `smartthings.0.${WASHER_ID}.status.main.washerOperatingState.machineState.value`)
  const washerJob         = val(washerStates, `smartthings.0.${WASHER_ID}.status.main.washerOperatingState.washerJobState.value`)
  const washerEnd         = val(washerStates, `smartthings.0.${WASHER_ID}.status.main.washerOperatingState.completionTime.value`)
  const washerPower       = val(washerStates, `smartthings.0.${WASHER_ID}.status.main.powerConsumptionReport.powerConsumption.value.power`)
  const washerRemote      = val(washerStates, `smartthings.0.${WASHER_ID}.status.main.remoteControlStatus.remoteControlEnabled.value`)
  const washerDoor        = val(washerStates, `smartthings.0.${WASHER_ID}.status.main.doorState.doorState.value`)
  // A booked delay end (min to the program end) and the selected program's length: while the
  // machine first detects the load, these tell a booked start from an immediate one.
  const washerDelayMin    = val(washerStates, `smartthings.0.${WASHER_ID}.status.main.samsungce.washerDelayEnd.remainingTime.value`)
  const washerProgramMin  = val(washerStates, `smartthings.0.${WASHER_ID}.status.main.samsungce.washerOperatingState.operationTime.value`)
  const dryerDelayMin     = val(dryerStates,  `smartthings.0.${DRYER_ID}.status.main.samsungce.dryerDelayEnd.remainingTime.value`)
  const dryerProgramMin   = val(dryerStates,  `smartthings.0.${DRYER_ID}.status.main.samsungce.dryerOperatingState.operationTime.value`)
  const dryerSwitch       = val(dryerStates,  `smartthings.0.${DRYER_ID}.status.main.switch.switch.value`)
  const dryerPower        = val(dryerStates,  `smartthings.0.${DRYER_ID}.status.main.powerConsumptionReport.powerConsumption.value.power`)
  const dryerRemote       = val(dryerStates,  `smartthings.0.${DRYER_ID}.status.main.remoteControlStatus.remoteControlEnabled.value`)
  const dryerMachineState = val(dryerStates,  `smartthings.0.${DRYER_ID}.status.main.dryerOperatingState.machineState.value`)
  const dryerEnd          = val(dryerStates,  `smartthings.0.${DRYER_ID}.status.main.dryerOperatingState.completionTime.value`)
  const dryerJob          = val(dryerStates,  `smartthings.0.${DRYER_ID}.status.main.dryerOperatingState.dryerJobState.value`)

  const idleStates = new Set(['stop', 'finish'])
  const washerLastFinishedTs = idleStates.has(washerState)
    ? lc(washerStates, `smartthings.0.${WASHER_ID}.status.main.washerOperatingState.machineState.value`)
    : null
  const dryerLastFinishedTs = idleStates.has(dryerMachineState)
    ? lc(dryerStates, `smartthings.0.${DRYER_ID}.status.main.dryerOperatingState.machineState.value`)
    : null

  const parseBool = (v) => v === true || v === 'true'

  return {
    tado1Rooms,
    tado0Rooms,
    solar: { acPower: solarAcPower, yieldToday: Math.round(solarYieldToday) },
    charger: {
      carStatus,
      carLabel: GO_E_CAR[carStatus] ?? 'Unknown',
      carConnected: carStatus === 2 || carStatus === 4,
      isCharging: chargePowerW > 0,
      allowCharging: !!allowCharging,
      power: chargePowerW / 1000,
      ampMqtt: wallboxAmpMqtt ?? null,
      totalKwh: chargeTotal,
      ampere: chargeAmpere,
      balancingProfile: val(settingsStates, '0_userdata.0.yokan-energy.settings.currentBalancingProfile') ?? null,
      useHouseBattery: val(settingsStates, '0_userdata.0.yokan-energy.settings.car.useHouseBattery') ?? true,
      minSoCThreshold: val(settingsStates, '0_userdata.0.yokan-energy.settings.car.minSoCThreshold') ?? 20,
      useHouseBatteryBonus: val(settingsStates, '0_userdata.0.yokan-energy.settings.car.useHouseBatteryBonus') ?? 0,
      maxBatteryDischargeThreshold: val(settingsStates, '0_userdata.0.yokan-energy.settings.car.maxBatteryDischargeThreshold') ?? 0,
      startPeakShavingThreshold: val(settingsStates, '0_userdata.0.yokan-energy.settings.car.startPeakShavingThreshold') ?? 30,
      carForceAmpere: carForceAmpere ?? 0,
    },
    appliances: {
      washer: {
        state: washerState ?? 'stop',
        job: washerJob,
        completionTime: washerEnd,
        lastFinishedTs: washerLastFinishedTs,
        power: washerPower ?? 0,
        active: washerState === 'run',
        finished: washerState === 'finish',
        remoteEnabled: parseBool(washerRemote),
        doorOpen: washerDoor === 'open',
        delayMin: Number(washerDelayMin) || 0,
        programMin: Number(washerProgramMin) || null,
      },
      dryer: {
        state: dryerSwitch ?? 'off',
        machineState: dryerMachineState ?? 'stop',
        job: dryerJob,
        delayMin: Number(dryerDelayMin) || 0,
        programMin: Number(dryerProgramMin) || null,
        completionTime: dryerEnd,
        lastFinishedTs: dryerLastFinishedTs,
        power: dryerPower ?? 0,
        active: dryerMachineState === 'run',
        finished: dryerMachineState === 'finish',
        remoteEnabled: parseBool(dryerRemote),
      },
    },
    garden: {
      temp:   val(nsPanelStates, 'alias.0.NSPanel.nspanel_garden.Sensor.ESP32.Temperature.ACTUAL'),
      lampOn: !!val(nsPanelStates, 'alias.0.NSPanel.Gartenhaus_Lampe.SET'),
      valves: parseValves(zigbeeStates),
    },
    plants: {
      planters: parsePlanters(zigbeeStates),
    },
    heatpump: parseHeatpump(heatpumpStates, heatpumpEmStates, statesMap[HUE_OUTDOOR_PATTERN] ?? {}),
  }
}

export async function fetchDashboardData() {
  const [
    solarStates, goeStates, washerStates, dryerStates,
    nsPanelStates, tado1States, tado2States, settingsStates,
    goeMqttStates, carForceAmpere, mideaStates, zigbeeStates,
    heatpumpStates, heatpumpEmStates, hueOutdoorStates,
  ] = await Promise.all([
    getStates('mqtt.0.solar.*'),
    getStates('go-e.0.*'),
    getStates(`smartthings.0.${WASHER_ID}.status.main.*`),
    getStates(`smartthings.0.${DRYER_ID}.status.main.*`),
    getStates('alias.0.NSPanel.*'),
    getStates(`tado.1.${TADO1_HOME}.Rooms.*`),
    getStates(`tado.1.${TADO_HOME2}.Rooms.*`),
    getStates('0_userdata.0.yokan-energy.settings.*'),
    getStates('mqtt.0.go-e.yokan.*'),
    apiFetch(`${import.meta.env.VITE_API_BASE ?? ''}/balance/carForceAmpere`).then(r => r.json()).catch(() => 0),
    getStates('mqtt.0.midea.portasplit.*'),
    getStates('mqtt.0.zigbee2mqtt.*'),
    getStates('mqtt.4.viessmann.vitocal.*'),
    getStates('shelly.0.shellypro3em#e05a1b333574#1.EM0.*'),
    getStates(HUE_OUTDOOR_PATTERN),
  ])

  return transformDashboardData({
    'mqtt.0.solar.*':    solarStates,
    'go-e.0.*':          goeStates,
    [`smartthings.0.${WASHER_ID}.status.main.*`]:  washerStates,
    [`smartthings.0.${DRYER_ID}.status.main.*`]:   dryerStates,
    'alias.0.NSPanel.*': nsPanelStates,
    [`tado.1.${TADO1_HOME}.Rooms.*`]:              tado1States,
    [`tado.1.${TADO_HOME2}.Rooms.*`]:              tado2States,
    '0_userdata.0.yokan-energy.settings.*':        settingsStates,
    'mqtt.0.go-e.yokan.*':                         goeMqttStates,
    'mqtt.0.midea.portasplit.*':                   mideaStates,
    'mqtt.0.zigbee2mqtt.*':                        zigbeeStates,
    'mqtt.4.viessmann.vitocal.*':                  heatpumpStates,
    'shelly.0.shellypro3em#e05a1b333574#1.EM0.*':  heatpumpEmStates,
    [HUE_OUTDOOR_PATTERN]:                          hueOutdoorStates,
  }, carForceAmpere)
}

const DISHWASHER_ID = '403010386675000429'
const BMW = 'bmw.0.WBY71AW050FS08184'

export async function fetchDishwasherData() {
  const states = await getStates(`homeconnect.0.${DISHWASHER_ID}.*`)
  const v = (key) => states[`homeconnect.0.${DISHWASHER_ID}.${key}`]?.val ?? null

  const operationState = v('status.BSH_Common_Status_OperationState') ?? ''
  const active = operationState.includes('Run') || operationState.includes('Active') || operationState.includes('DelayedStart')
  const opStateTs = states[`homeconnect.0.${DISHWASHER_ID}.status.BSH_Common_Status_OperationState`]?.ts ?? null
  const lastFinishedTs = !active && (operationState.includes('Finished') || operationState.includes('Ready')) ? opStateTs : null
  const progress = v('programs.active.options.BSH_Common_Option_ProgramProgress') ?? 0
  const remainingSec = v('programs.active.options.BSH_Common_Option_RemainingProgramTime') ?? 0
  const program = v('programs.selected.BSH_Common_Root_SelectedProgram') ?? ''
  const programName = program.split('.').pop()?.replace(/([A-Z])/g, ' $1').trim() ?? ''
  const doorOpen = (v('status.BSH_Common_Status_DoorState') ?? '').includes('Open')
  const saltLow = (v('events.Dishcare_Dishwasher_Event_SaltNearlyEmpty') ?? '').includes('Present')
  const rinseAidLow = (v('events.Dishcare_Dishwasher_Event_RinseAidNearlyEmpty') ?? '').includes('Present')
  const remoteStartAllowed = v('status.BSH_Common_Status_RemoteControlStartAllowed') === true

  return {
    active,
    operationState: operationState.split('.').pop() ?? '',
    progress,
    remainingSec,
    programName,
    doorOpen,
    saltLow,
    rinseAidLow,
    lastFinishedTs,
    remoteStartAllowed,
    connected: v('general.connected'),
  }
}

export async function fetchBMWData() {
  const res = await fetch('/api/bmw')
  if (!res.ok) throw new Error(`BMW API ${res.status}`)
  return res.json()
}

export async function fetchBrightSkyForecast() {
  const slots = ['00', '01', '02', '03', '04', '05', '06', '07']
  const states = await getStates('brightsky.0.daily.*')
  return slots.map(slot => {
    const v = (key) => states[`brightsky.0.daily.${slot}.${key}`]?.val ?? null
    return {
      slot,
      tempMax:   v('temperature_max'),
      tempMin:   v('temperature_min'),
      icon:      v('icon'),
      condition: v('condition'),
      solar:     v('solar'),
    }
  })
}

export async function fetchBrightSkyWeather() {
  const [hourlyStates, currentStates] = await Promise.all([
    getStates('brightsky.0.hourly.00.*'),
    getStates('brightsky.0.current.*'),
  ])
  const v = (key) => hourlyStates[`brightsky.0.hourly.00.${key}`]?.val ?? null
  return {
    temperature:  v('temperature'),
    condition:    v('condition'),
    icon:         v('icon'),
    windSpeed:    v('wind_speed'),
    windDirection:v('wind_direction'),
    humidity:     v('humidity'),
    precipitation:v('precipitation'),
    cloudCover:   v('cloud_cover'),
    pressure:     v('pressure_msl'),
    visibility:   v('visibility'),
    solar10:      currentStates['brightsky.0.current.solar_10']?.val ?? null,
    windGust10:   currentStates['brightsky.0.current.wind_gust_speed_10']?.val ?? null,
  }
}

export async function fetchSunData() {
  const states = await getStates('followthesun.0.*')
  const v = (key) => states[`followthesun.0.${key}`]?.val ?? null
  // Today's times live under "short term.today.*_time" as epoch ms (there is no
  // current.sunrise etc.); handed on as ISO strings, which every consumer parses.
  const time = (key) => { const ms = Number(v(`short term.today.${key}_time`)); return ms > 0 ? new Date(ms).toISOString() : null }
  return {
    state:     v('current'),
    azimuth:   v('current.azimuth'),
    altitude:  v('current.altitude'),
    sunrise:   time('sunrise'),
    sunset:    time('sunset'),
    solarNoon: time('solarnoon'),
    dawn:      time('dawn'),
    dusk:      time('dusk'),
  }
}

// Tahoma states now come from the backend /api/tahoma boundary (Local API or the
// ioBroker fallback — the frontend doesn't care which). Shape:
//   { states: { [key]: { closure, moving } }, aliases: { [alias]: key } }
async function fetchTahomaStates() {
  try {
    const r = await apiFetch(`${TAHOMA_BASE}/states`)
    if (!r.ok) return { states: {}, aliases: {} }
    return await r.json()
  } catch {
    return { states: {}, aliases: {} }
  }
}

// Write a shutter/awning command through the backend boundary.
export async function sendTahomaCommand(key, payload) {
  const res = await apiFetch(`${TAHOMA_BASE}/command/${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw Object.assign(new Error('tahoma-command-failed'), { status: res.status })
}

// Only the Shelly cover states, from the server's shared cache (not the whole tree).
async function fetchShellyShutterStates() {
  try {
    const r = await apiFetch(`${BASE}/shutter-states`)
    return r.ok ? await r.json() : {}
  } catch {
    return {}   // Tahoma shutters still show when the Shelly side is unreachable
  }
}

export async function fetchShutterStates() {
  const [tahoma, shellyStates] = await Promise.all([
    fetchTahomaStates(),
    fetchShellyShutterStates(),
  ])
  const tStates  = tahoma.states  ?? {}
  const tAliases = tahoma.aliases ?? {}
  const resolveT = (key) => tStates[key] ? key : (tAliases[key] ?? key)

  // Synthesize a Tahoma raw map so ShutterCard's device-key enumeration (which
  // scans `tahoma.0.devices.<key>.states.core:ClosureState`) keeps working unchanged.
  const tahomaRaw = {}
  for (const [key, s] of Object.entries(tStates)) {
    tahomaRaw[`tahoma.0.devices.${key}.states.core:ClosureState`] = { val: s.closure }
    if (s.moving) tahomaRaw[`tahoma.0.devices.${key}.states.moving`] = { val: true }
  }

  const closure = (key) => {
    if (key.startsWith('shelly:')) {
      const base = key.slice(7)
      // Gen1 (SHSW-25): .Shutter.Position  Gen2 (Plus 2PM): .Cover0.CurrentPos or .Pos
      const pos = shellyStates[`${base}.Position`]?.val
        ?? shellyStates[`${base}.CurrentPos`]?.val
        ?? shellyStates[`${base}.Pos`]?.val
      return pos != null ? Math.round(100 - pos) : null
    }
    const v = tStates[resolveT(key)]?.closure
    return v != null ? Math.round(v) : null
  }

  const moving = (key) => {
    if (key.startsWith('shelly:')) {
      const base = key.slice(7)
      const state = shellyStates[`${base}.state`]?.val ?? shellyStates[`${base}.State`]?.val ?? ''
      return state === 'open' || state === 'close'
    }
    return !!tStates[resolveT(key)]?.moving
  }

  return { closure, moving, raw: { ...tahomaRaw, ...shellyStates } }
}

export async function fetchPlainValue(id) {
  return getPlain(id)
}

export async function fetchHouseBatterySoC() {
  const states = await getStates('mqtt.0.victron.battery.EEL.*')
  const { avgSoc, avgTemperature, avgVoltage } = aggregateEelStates(states)
  return {
    soc:         avgSoc ?? states['mqtt.0.victron.battery.EEL.SOC']?.val ?? null,
    temperature: avgTemperature,
    voltage:     avgVoltage,
  }
}

// Live go-e Wallbox charging power from MQTT (in watts).
export async function fetchWallboxLive() {
  const [nrg, car, wo] = await Promise.all(['nrg', 'car', 'wo'].map(k => getPlain(`mqtt.0.go-e.yokan.${k}`)))
  const w = goePowerW({ nrg, car, wo })
  if (w != null) return { powerW: w }
  const kw = await getPlain('go-e.0.energy.power')
  return { powerW: kw != null ? kw * 1000 : 0 }
}

// Live battery power and SoC from ioBroker — used by cards that need
// real-time battery state without going through InfluxDB.
export async function fetchHouseBatteryLive() {
  const states = await getStates('mqtt.0.victron.battery.EEL.*')
  const { sumPower, avgSoc } = aggregateEelStates(states)
  return {
    power: sumPower ?? 0,
    soc:   avgSoc ?? states['mqtt.0.victron.battery.EEL.SOC']?.val ?? null,
  }
}

export async function fetchBalanceCalcData() {
  const [
    victronStates,
    goeStates,
    settingsStates,
    goeMqttStates,
    chargingStartTS,
  ] = await Promise.all([
    getStates('mqtt.0.victron.*'),
    getStates('go-e.0.*'),
    getStates('0_userdata.0.yokan-energy.settings.*'),
    getStates('mqtt.0.go-e.yokan.*'),
    getPlain('0_userdata.0.car.0.isChargingStartTS'),
  ])
  // Its publisher can freeze on its last watts (17.10.2026: 135 W into the night).
  const pvPower = freshPvW(victronStates['mqtt.0.victron.totalPVPower'])

  const { avgSoc, sumPower } = aggregateEelStates(victronStates)
  const batterySoC   = avgSoc   ?? val(victronStates, 'mqtt.0.victron.battery.EEL.SOC')
  const batteryPower = sumPower  ?? val(victronStates, 'mqtt.0.victron.battery.EEL.Power')
  const wallboxPowerW = goePowerW({ nrg: val(goeMqttStates, 'mqtt.0.go-e.yokan.nrg'), car: val(goeMqttStates, 'mqtt.0.go-e.yokan.car'), wo: val(goeMqttStates, 'mqtt.0.go-e.yokan.wo') })

  return {
    gridPower:        val(victronStates, 'mqtt.0.victron.em24.grid_power'),
    batteryPower,
    batterySoC,
    pvPower,
    chargePower:      wallboxPowerW != null ? wallboxPowerW : (val(goeStates, 'go-e.0.energy.power') ?? 0) * 1000,
    // Wallbox phase setting (psm 1 = one phase) — the balance switches it (server wallboxPhases.js)
    phases:           Number(val(goeMqttStates, 'mqtt.0.go-e.yokan.psm')) === 1 ? 1 : 3,
    ampereSet:        val(goeStates,    'go-e.0.ampere'),
    allowCharging:    val(goeStates,    'go-e.0.allow_charging'),
    v1:               val(goeStates,    'go-e.0.energy.phase1.voltage'),
    v2:               val(goeStates,    'go-e.0.energy.phase2.voltage'),
    v3:               val(goeStates,    'go-e.0.energy.phase3.voltage'),
    profileKey:                val(settingsStates, '0_userdata.0.yokan-energy.settings.currentBalancingProfile'),
    useHouseBattery:           val(settingsStates, '0_userdata.0.yokan-energy.settings.car.useHouseBattery') ?? true,
    minSoCThreshold:           val(settingsStates, '0_userdata.0.yokan-energy.settings.car.minSoCThreshold') ?? 20,
    useHouseBatteryBonus:      val(settingsStates, '0_userdata.0.yokan-energy.settings.car.useHouseBatteryBonus') ?? 0,
    startPeakShavingThreshold: val(settingsStates, '0_userdata.0.yokan-energy.settings.car.startPeakShavingThreshold') ?? 30,
    chargingStartTS:           chargingStartTS ?? 0,
  }
}

export async function getValveState(device) {
  const data = await getPlain(`mqtt.0.zigbee2mqtt.${device}`)
  return data?.state ?? null  // 'ON' | 'OFF' | null
}

export async function getShellySwitch(ioBrokerId) {
  return getPlain(`${ioBrokerId}.Relay0.Switch`)  // true | false | null
}

export async function setIoBrokerState(id, value) {
  const res = await apiFetch(`${BASE}/set/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value }),
  })
  if (!res.ok) throw Object.assign(new Error('write-failed'), { status: res.status })
}

// Washer, dryer and dishwasher start through the server (services/applianceRemote.js):
// the vendors' generic start commands are ignored, and a scheduled start needs the
// machine's own delay read back before the start command goes out.
async function applianceCommand(device, action, body = {}) {
  const res = await apiFetch(`${import.meta.env.VITE_API_BASE ?? ''}/appliances/${device}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
  return data
}

/**
 * `device`: washer | dryer | dishwasher. `start`: ISO time, or null for now; or
 * `{ end }` to have it finish then (night runs). At most 23 h ahead.
 */
export const startAppliance = (device, start = null, { end = null } = {}) => applianceCommand(device, 'start', { start, end })
export const cancelApplianceStart = device => applianceCommand(device, 'cancel')

export async function setValve(device, on) {
  const res = await apiFetch(`${import.meta.env.VITE_API_BASE ?? ''}/mqtt/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ topic: `zigbee2mqtt/${device}/set`, value: { state: on ? 'ON' : 'OFF' } }),
  })
  if (!res.ok) throw Object.assign(new Error('valve-write-failed'), { status: res.status })
}


function mideaSetPrefix(topicPrefix = 'midea/portasplit') {
  return `mqtt.0.${topicPrefix.replace(/\//g, '.')}.set`
}

export async function setMideaPower(on, topicPrefix) {
  await setIoBrokerState(`${mideaSetPrefix(topicPrefix)}.power`, on ? 'on' : 'off')
}

export async function setMideaTemp(temp, topicPrefix) {
  await setIoBrokerState(`${mideaSetPrefix(topicPrefix)}.temp`, String(Math.round(temp)))
}

export async function setMideaMode(mode, topicPrefix) {
  await setIoBrokerState(`${mideaSetPrefix(topicPrefix)}.mode`, mode)
}

export async function setMideaFanSpeed(speed, topicPrefix) {
  await setIoBrokerState(`${mideaSetPrefix(topicPrefix)}.fan_speed`, speed)
}

export async function setMideaSwingMode(mode, topicPrefix) {
  await setIoBrokerState(`${mideaSetPrefix(topicPrefix)}.swing_mode`, mode)
}

export async function setMideaTurbo(on, topicPrefix) {
  await setIoBrokerState(`${mideaSetPrefix(topicPrefix)}.turbo`, on ? 'true' : 'false')
}

export async function setMideaEco(on, topicPrefix) {
  await setIoBrokerState(`${mideaSetPrefix(topicPrefix)}.eco`, on ? 'true' : 'false')
}
